import React, { createContext, useContext, useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const TYPE_CONFIG = {
  success: { Icon: CheckCircle2, accent: '#1dbf73', ring: 'rgba(29,191,115,0.4)' },
  error: { Icon: AlertCircle, accent: '#ef4444', ring: 'rgba(239,68,68,0.4)' },
  info: { Icon: Info, accent: '#3b82f6', ring: 'rgba(59,130,246,0.4)' },
};

/**
 * Système de toast léger et réutilisable (aucune dépendance externe).
 * Usage : const toast = useToast(); toast.success('...'), toast.error('...'), toast.info('...')
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (type, message, opts = {}) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, type, message, title: opts.title }]);
      // Auto-fermeture
      setTimeout(() => remove(id), opts.duration ?? 4500);
      return id;
    },
    [remove]
  );

  const toast = useRef({
    success: (msg, opts) => push('success', msg, opts),
    error: (msg, opts) => push('error', msg, opts),
    info: (msg, opts) => push('info', msg, opts),
  }).current;

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Conteneur des toasts — fixe en haut à droite, au-dessus de tout */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none max-w-[calc(100vw-2rem)]">
        <AnimatePresence>
          {toasts.map((t) => {
            const cfg = TYPE_CONFIG[t.type] || TYPE_CONFIG.info;
            const Icon = cfg.Icon;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, x: 40, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="pointer-events-auto flex items-start gap-3 bg-primary-card border border-primary-sidebar rounded-xl shadow-2xl px-4 py-3 min-w-[280px]"
                style={{ boxShadow: `0 8px 30px ${cfg.ring}` }}
              >
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${cfg.accent}22` }}
                >
                  <Icon size={18} style={{ color: cfg.accent }} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  {t.title && (
                    <p className="text-white font-semibold text-sm leading-tight mb-0.5">{t.title}</p>
                  )}
                  <p className="text-text-gray text-sm leading-snug break-words">{t.message}</p>
                </div>
                <button
                  onClick={() => remove(t.id)}
                  className="flex-shrink-0 text-text-gray hover:text-white transition-colors"
                  aria-label="Fermer"
                >
                  <X size={16} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast doit être utilisé à l’intérieur de <ToastProvider>');
  }
  return ctx;
}

export default ToastContext;
